// src/AccountPage.jsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Card,
  Box,
  Typography,
  Container,
  Snackbar,
  MenuItem,
  Alert,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import DirectorNavbar from "./navigation";
import NavigationTalent from "./talent/NavigationTalent";
import Navbar from "./admin/AdminNav";
import PageInstructions from "./talent/components/PageInstructions";
import PhoneNumberField from "./components/common/PhoneNumberField";

import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";

import Footer from "./Footer";
import AppLoader from "./components/common/AppLoader";
import PaymentForm from "./components/PaymentForm";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import GlobalNotification from "./components/GlobalNotification";

const AccountPage = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const [subscription, setSubscription] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [locations, setLocations] = useState([]);
  const [phoneError, setPhoneError] = useState("");
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const userRole = localStorage.getItem("userRole");

  const isDirector = userRole === "COMPANY";
  const isTalent = userRole === "USER";
  const isAdmin = userRole === "ADMIN";

  const [userId, setUserId] = useState(null);

  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMsg(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  /* ================= LOAD ACCOUNT ================= */

  const loadAccount = () => {
    const email = localStorage.getItem("email")?.toLowerCase();
    if (!email) return;

    setLoading(true);

    api
      .get(ENDPOINTS.getAccountPage({ email }))
      .then((res) => {
        if (res.data.status === "Ok") {
          const account = res.data.data[0];
          setData({
            ...account,
            fullname:
              `${account.firstName || ""} ${account.lastName || ""}`.trim(),
          });
          if (account.logo) {
            setLogoPreview(account.logo);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  /* ================= LOAD SUBSCRIPTION ================= */

  const loadSubscription = () => {
    const email = localStorage.getItem("email")?.toLowerCase();
    if (!email) return;

    api
      .get(`/api/Admin/GetSubscriptionByEmail?email=${email}`)
      .then((res) => {
        if (res.data.status === "Success") {
          const sub = res.data.data;
          setSubscription(sub);
          console.log("Subscription data:", sub);
setSubscriptionActive(sub.status?.toLowerCase() === "active");
        } else {
          setSubscriptionActive(false);
        }
      })
      .catch(() => setSubscriptionActive(false));
  };

  useEffect(() => {
    loadAccount();
    loadSubscription();
    loadLocations();

    const params = new URLSearchParams(location.search);
    const state = params.get("state");

    if (state === "succeeded") {
      showSnackbar("Subscription Activated");

       setTimeout(() => {
    loadSubscription();
    window.dispatchEvent(new Event("client-details-updated"));
  }, 1500);

      navigate("/account", { replace: true });
    }
  }, []);

  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) setUserId(id);
  }, []);

  const loadLocations = () => {
    api.get(ENDPOINTS.getLocations()).then((res) => {
      if (res.data.status === "Ok") {
        setLocations(res.data.data);
      }
    });
  };

  /* ================= LOGO ================= */

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];

    if (!allowed.includes(file.type)) {
      showSnackbar("Only JPG, PNG or WEBP allowed", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Max file size is 5MB", "error");
      return;
    }

    setSelectedLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };
  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);

    // optional reset
    setPasswordData({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors({});
  };
  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };
  const validatePassword = () => {
    const errors = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = "Required";
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "Required";
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = "Minimum 8 characters";
    } else if (!/[A-Z]/.test(passwordData.newPassword)) {
      errors.newPassword = "Must include uppercase letter";
    } else if (!/[0-9]/.test(passwordData.newPassword)) {
      errors.newPassword = "Must include a number";
    }

    if (passwordData.confirmPassword !== passwordData.newPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleChangePassword = async () => {
    const email = localStorage.getItem("email");

    if (!validatePassword()) return;

    try {
      setPasswordLoading(true);

      await api.post("/api/User/ChangePassword", {
        email,
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      showSnackbar("Password changed successfully");
      handleClosePasswordDialog();

      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setPasswordErrors({});
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || "Failed to change password",
        "error",
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteProfile = async () => {
    try {
      setDeleteLoading(true);

      await api.post("/api/Admin/UpdateUserResumeStatus", {
        userId: data.userId, // make sure this exists in your account data
        isDelete: true,
      });

      showSnackbar("Profile deleted successfully");

      // optional: logout / redirect
      localStorage.clear();
      navigate("/login");
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || "Failed to delete profile",
        "error",
      );
    } finally {
      setDeleteLoading(false);
      setOpenDeleteDialog(false);
    }
  };
  const isValidPhone = (value = "") => {
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= 7;
};

/* ================= SAVE ================= */
const handleSave = () => {
  if (!data.phoneNumber) {
    setPhoneError("Phone number is required");
    return;
  }

  if (!isValidPhone(data.phoneNumber)) {
    setPhoneError("Enter a valid phone number");
    return;
  }

    const formData = new FormData();

    // Split full name
    const fullName = (data.fullname || "").trim();
    const parts = fullName.split(/\s+/);

    const firstName = parts.shift() || "";
    const lastName = parts.join(" ") || "";

    // Required fields
    formData.append("userId", data.userId);
    formData.append("contactEmail", data.contactEmail);

    if (data.password) {
      formData.append("password", data.password);
    }
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("companyName", data.companyName || "");
    formData.append("phonNumber", data.phoneNumber || "");
    formData.append("address1", data.address1 || "");
    formData.append("address2", data.address2 || "");
    formData.append("city", data.city || "");
    formData.append("state1", data.state1 || "");
    formData.append("zip", data.zip || "");

    if (selectedLogo) {
      formData.append("Logo", selectedLogo);
    }

    formData.append("FileURL", data.logo || "");

    api
      .post(ENDPOINTS.updateClientDetails(), formData)
      .then(() => {
        showSnackbar("Saved successfully");
        loadAccount();
      })
      .catch(() => showSnackbar("Save failed", "error"));
  };
  /* ================= BILLING ================= */

  const handleManageBilling = () => {
    const email = localStorage.getItem("email");

    api
      .post(`/api/Admin/edit-billing?email=${email}`)
      .then((res) => {
        const url = res.data.redirectUrl;
        if (url) window.open(url, "_blank");
      })
      .catch(() => showSnackbar("Billing failed", "error"));
  };

  /* ================= NAVBAR ================= */

  const renderNavbar = () => {
    if (isDirector) return <DirectorNavbar />;
    if (isTalent) return <NavigationTalent />;
    return <Navbar />;
  };

  if (loading) return <AppLoader message="Loading..." />;

  return (
    <>
      {renderNavbar()}

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {userRole !== "ADMIN" && (
          <GlobalNotification userId={userId} userRole={userRole} />
        )}
        <Typography variant="h5" mb={3}>
          My Account
        </Typography>

        {/* ================= DIRECTOR ================= */}
        {isDirector && (
          <>
            <Box display="flex" gap={4} flexWrap="wrap">
              {/* PERSONAL */}
              <Card sx={{ flex: 1, minWidth: 350, p: 3 }}>
                <Typography variant="h6">Personal</Typography>

                <TextField
                  label="Username"
                  value={data.loginName || ""}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Email"
                  value={data.contactEmail || ""}
                  fullWidth
                  margin="normal"
                />
                <PhoneNumberField
                  phoneNumber={data.phoneNumber || ""}
                  onChange={({ phoneNumber }) => {
                    setData((prev) => ({
                      ...prev,
                      phoneNumber,
                    }));

                    if (!phoneNumber) {
                      setPhoneError("Phone number is required");
                    } else {
                      setPhoneError("");
                    }
                  }}
                  error={!!phoneError}
                  helperText={phoneError}
                  required
                  className="marginPhoneNumberField"
                />
                <Box mt={2}>
                  <Button
                    variant="text"
                    onClick={handleOpenPasswordDialog}
                    sx={{ textTransform: "none" }}
                  >
                    Change Password
                  </Button>
                </Box>
              </Card>

              {/* COMPANY */}
              <Card sx={{ flex: 1, minWidth: 350, p: 3 }}>
                <Typography variant="h6">Company</Typography>
                <TextField
                  label="Full Name"
                  value={data.fullname || ""}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      fullname: e.target.value,
                    }))
                  }
                  fullWidth
                  margin="normal"
                />

                <TextField
                  label="Company Name"
                  value={data.companyName || ""}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      companyName: e.target.value,
                    }))
                  }
                  fullWidth
                  margin="normal"
                />

                <Box display="flex" gap={2}>
                  <TextField
                    select
                    label="City, State"
                    fullWidth
                    margin="normal"
                    value={data.city || ""} // city holds locationCode, e.g. "NY1"
                    onChange={(e) => {
                      const selectedCode = e.target.value;
                      const loc = locations.find(
                        (l) => l.locationCode === selectedCode,
                      );

                      setData((prev) => ({
                        ...prev,
                        city: loc?.locationCode || "", // e.g. "NY1"
                        state1: loc?.locationStateAbbreviatoion || "", // e.g. "NY"
                      }));
                    }}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.locationCode} value={loc.locationCode}>
                        {loc.locationCity} ({loc.locationStateAbbreviatoion})
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/*  <Box display="flex" gap={2} mt={2}>
                  <TextField
                    label="Zip Code"
                    value={data.zip || ""}
                    onChange={(e) =>
                      setData((prev) => ({ ...prev, zip: e.target.value }))
                    }
                    fullWidth
                  />
                </Box> */}
                <Button
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{ mt: 2 }}
                >
                  Upload Logo
                  <input hidden type="file" onChange={handleLogoChange} />
                </Button>

                {logoPreview && (
                  <Box mt={2}>
                    <img src={logoPreview} style={{ maxWidth: 120 }} />
                  </Box>
                )}
              </Card>

              {/* SUBSCRIPTION */}
              {subscriptionActive && subscription && (
                <Card sx={{ flex: 1, minWidth: 350, p: 3 }}>
                  <Typography variant="h6">Plan</Typography>

                  <Typography>Plan: {subscription.planName}</Typography>
                  <Typography>Status: {subscription.status}</Typography>

                  <Button sx={{ mt: 2 }} onClick={handleManageBilling}>
                    Manage Billing
                  </Button>
                </Card>
              )}
            </Box>

            <Box textAlign="center" mt={3}>
              <Button variant="contained" onClick={handleSave}>
                Save Changes
              </Button>
            </Box>
          </>
        )}

        {/* ================= TALENT ================= */}
        {isTalent && (
          <>
            <PageInstructions storageKey="resumeEditInstructions">
              <strong>Manage Your Account & Subscription</strong>
              <br />
              Update your account details, change your password, or choose a
              subscription plan after your free trial has ended. You can
              upgrade, downgrade, or cancel at any time.
            </PageInstructions>

            <Box
              display="flex"
              gap={4}
              flexWrap="wrap"
              sx={{
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
              }}
            >
              <Card
                sx={{
                  flex: 1,
                  minWidth: 350,
                  p: 3,
                  order: {
                    xs: 2,
                    md: 1,
                  },
                }}
              >
                <Typography variant="h6">Account</Typography>
                <TextField
                  label="Username"
                  value={data.loginName || ""}
                  fullWidth
                  margin="normal"
                />{" "}
                <TextField
                  label="Status"
                  value={
                    (data.userStatus || "").toLowerCase() === "inactive" &&
                    Number(data.userCredits) > 0
                      ? "Free Trial"
                      : data.userStatus || ""
                  }
                  disabled
                  fullWidth
                  margin="normal"
                />
                {!subscriptionActive && (
                  <TextField
                    label="Credits"
                    value={data.userCredits || 0}
                    disabled
                    fullWidth
                    margin="normal"
                  />
                )}
                {!subscriptionActive && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: {
                          xs: "stretch",
                          sm: "flex-start",
                        },
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          lineHeight: 1.7,
                        }}
                      >
                        Credits are used to submit to casting notices. Each
                        submission uses one credit. Your account includes a
                        limited number of free trial credits before a membership
                        subscription is required.
                      </Typography>

                      {/* <Button
    onClick={handleSave}
    variant="contained"
    sx={{
      width: {
        xs: "100%",
        sm: "auto",
      },
      alignSelf: {
        xs: "stretch",
        sm: "flex-start",
      },
    }}
  >
    Save
  </Button> */}
                    </Box>
                  </>
                )}
                <Box mt={2}>
                  <Button
                    variant="text"
                    onClick={handleOpenPasswordDialog}
                    sx={{ textTransform: "none" }}
                  >
                    Change Password
                  </Button>
                </Box>
                <Box mt={1}>
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleOpenDeleteDialog}
                    sx={{ textTransform: "none" }}
                  >
                    Delete Profile
                  </Button>
                </Box>
              </Card>
              {!subscriptionActive && (
                <Card
                  sx={{
                    flex: 1,
                    minWidth: 350,
                    p: 3,
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  <PaymentForm email={data.contactEmail} />
                </Card>
              )}

              {subscriptionActive && (
                <Card
                  sx={{
                    flex: 1,
                    minWidth: 350,
                    p: 3,
                    order: {
                      xs: 1,
                      md: 2,
                    },
                  }}
                >
                  <Typography>Active Plan: {subscription.planName}</Typography>
                  <Button onClick={handleManageBilling}>Manage Billing</Button>
                </Card>
              )}
            </Box>
          </>
        )}

        {/* ================= ADMIN ================= */}
        {isAdmin && (
          <>
            <Card sx={{ p: 3 }}>
              <Typography>Admin: {data.contactEmail}</Typography>
            </Card>
            <Box mt={2}>
              <Button
                variant="text"
                onClick={handleOpenPasswordDialog}
                sx={{ textTransform: "none" }}
              >
                Change Password
              </Button>
            </Box>
          </>
        )}

        <Dialog
          open={openPasswordDialog}
          onClose={handleClosePasswordDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Change Password</DialogTitle>

          <DialogContent>
            {/* OLD PASSWORD */}
            <TextField
              label="Old Password"
              type={showPassword.old ? "text" : "password"}
              fullWidth
              margin="normal"
              value={passwordData.oldPassword}
              error={!!passwordErrors.oldPassword}
              helperText={passwordErrors.oldPassword}
              onChange={(e) =>
                handlePasswordChange("oldPassword", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("old")}>
                      {showPassword.old ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* NEW PASSWORD */}
            <TextField
              label="New Password"
              type={showPassword.new ? "text" : "password"}
              fullWidth
              margin="normal"
              value={passwordData.newPassword}
              error={!!passwordErrors.newPassword}
              helperText={
                passwordErrors.newPassword ||
                "Min 8 chars, 1 uppercase, 1 number"
              }
              onChange={(e) =>
                handlePasswordChange("newPassword", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("new")}>
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* CONFIRM PASSWORD */}
            <TextField
              label="Confirm Password"
              type={showPassword.confirm ? "text" : "password"}
              fullWidth
              margin="normal"
              value={passwordData.confirmPassword}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
              onChange={(e) =>
                handlePasswordChange("confirmPassword", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("confirm")}>
                      {showPassword.confirm ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClosePasswordDialog}>Cancel</Button>

            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={passwordLoading}
            >
              {passwordLoading ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Profile</DialogTitle>

          <DialogContent>
            <Typography>
              Are you sure you want to delete your profile? This action cannot
              be undone.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteProfile}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>{snackbarMsg}</Alert>
      </Snackbar>
    </>
  );
};

export default AccountPage;
